import { FC, useCallback, useMemo } from "react";
import { DashboardV2ItemMoveActionMenuFolderItem } from "./DashboardV2ItemMoveActionMenuFolderItem";
import { HierarchyItemMoveActionSection } from "../../../../../hierarchy/HierarchyItemMoveActionSection";
import { NavigationItemsVisibilityType } from "@/store/navigation/utils/types";
import { useAppSelector } from "@/store/hooks";
import { dashboardHierarchyFoldersSelector } from "@/store/dashboards/selectors/dashboard-hierarchy/dashboardHierarchyFoldersSelector";
import { dashboardFlattenHierarchyMixedVisibilityItemByIdSelector } from "@/store/dashboards/selectors/dashboard-hierarchy-visibility/dashboardFlattenHierarchyMixedVisibilityItemByIdSelector";
import { currentUserSelector } from "@/store/profile/currentUserSelector";

interface DashboardV2ItemMoveActionMenuSectionProps {
  hideTitle?: boolean;
  folderId?: string;
  currentDashboardName: string;
  visibility: NavigationItemsVisibilityType;
  currentVisibility: NavigationItemsVisibilityType;
  moveToFolder(
    visibility: NavigationItemsVisibilityType,
    folderId?: string,
  ): void;
  moveToRoot(visibility: NavigationItemsVisibilityType): void;
  id?: string;
}

export const DashboardV2ItemMoveActionMenuSection: FC<
  DashboardV2ItemMoveActionMenuSectionProps
> = ({
  folderId,
  visibility,
  currentVisibility,
  currentDashboardName,
  moveToFolder,
  moveToRoot,
  hideTitle,
  id,
}) => {
  const folders = useAppSelector((state) =>
    dashboardHierarchyFoldersSelector(state, visibility),
  );

  const dashboard = useAppSelector((state) =>
    dashboardFlattenHierarchyMixedVisibilityItemByIdSelector(state, id),
  );
  const createdBy = dashboard?.created_by;
  const currentUser = useAppSelector(currentUserSelector);

  const disabled =
    visibility === "visible_only_to_me" && createdBy?.id !== currentUser?.id;
  const tooltipTitle = disabled ? tooltipTitleValue : "";

  const filteredFolders = useMemo(() => {
    return visibility === currentVisibility && folderId
      ? folders?.filter((item) => item.id !== folderId)
      : folders;
  }, [folders, currentVisibility, visibility, folderId]);

  const moveHandler = useCallback(
    (folderId?: string) => {
      if (folderId) {
        moveToFolder(visibility, folderId);
      } else {
        moveToRoot(visibility);
      }
    },
    [moveToRoot, moveToFolder, visibility],
  );

  return (
    <HierarchyItemMoveActionSection
      onMove={moveHandler}
      hideTitle={hideTitle}
      visibility={visibility}
      folders={filteredFolders}
      inFolder={!!folderId}
      currentItemName={currentDashboardName}
      differentVisibilities={visibility !== currentVisibility}
      HierarchyItemMoveActionItem={DashboardV2ItemMoveActionMenuFolderItem}
      disabled={disabled}
      tooltipTitle={tooltipTitle}
    />
  );
};

const tooltipTitleValue =
  "Only the report creator has permission to change the visibility of the dashboard.";
